import { useNavigate, useSearchParams } from "react-router";
import { SkyButton } from "@skypointcloud/skypoint-ui";

import { ROUTES } from "../shared/constant";

/**
 * The app's return affordance (SW-CRM-GLOBAL-SETUP Inc 1f).
 *
 * Rendered once at the AppLayout content level, it appears ONLY when the current
 * page was reached from a known origin, declared by that origin as `?from=…`.
 * That solves the shared-page subtlety: Users/Roles (reachable from the sidebar
 * too) show the link only when arrived via the hub, never permanently. `gsi`
 * carries the origin's instance id so the return works even from Advisor pages
 * that have no instance in their path.
 *
 * Uses the exact SkyButton back-affordance from PersonDetailPage
 * (appearance="transparent", size="small", text arrow) so it reads identically
 * to the "Back to Prospects" button and every other back link in the app.
 *
 * ⚠️ ORIGINS ARE A TABLE, NOT AN `if`. It began as a single hardcoded "Back to
 * Setup", and the second caller — /ad-campaigns sending operators to Marketing
 * Integrations to connect Google Ads — stranded them there with no way back,
 * because the destination is a CRM settings page and the origin is an Advisor
 * page. Adding a second bespoke back-link component would have put two of these
 * in the layout, each ignorant of the other. One table, one component.
 */
interface BackOrigin {
  /** Where to return to. `gsi`, when present, is appended as a path segment. */
  route: string;
  label: string;
  /** Append the instance id as a path segment (hub-style) or leave the route bare. */
  appendInstanceToPath?: boolean;
  /** Append `?instanceId=…` instead — what Advisor routes expect. */
  appendInstanceQuery?: boolean;
}

/**
 * `setup` is what the hub emits today; `global-setup` is what it emitted before
 * the rename and is accepted FOREVER — spoke URLs carrying it are in bookmarks,
 * in shared links and in the manual test sheet, and dropping it would silently
 * strip their back button.
 */
/**
 * The ADVISOR Setup hub's marker.
 *
 * ⚠️ EXPORTED, so the hub that WRITES it and this table that READS it are the
 * same string. The CRM pair above are two literals that happen to match; that
 * works until someone edits one of them, and the failure mode is silent — the
 * spoke simply loses its back button. One constant removes the question.
 */
export const ADVISOR_SETUP_BACK_ORIGIN = "settings-setup";

const BACK_ORIGINS: Record<string, BackOrigin> = {
  "setup": { route: ROUTES.CRM_SETUP, label: "Back to Setup", appendInstanceToPath: true },
  "global-setup": { route: ROUTES.CRM_SETUP, label: "Back to Setup", appendInstanceToPath: true },
  /**
   * ⚠️ `appendInstanceToPath`, matching CRM_SETUP — SETTINGS_SETUP is declared
   * as `/settings/setup` + ROUTES.QUERYPARAMS (an optional ":instanceid?" PATH
   * segment), so the hub's own address carries the instance in the path. The
   * Advisor SPOKES take `?instanceid=`, but this route is the hub itself.
   */
  [ADVISOR_SETUP_BACK_ORIGIN]: {
    route: ROUTES.SETTINGS_SETUP,
    label: "Back to Setup",
    appendInstanceToPath: true,
  },
  "ad-campaigns": {
    route: ROUTES.AD_CAMPAIGNS,
    label: "Back to Ad Campaigns",
    appendInstanceQuery: true,
  },
};

const GlobalSetupBackLink = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get("from");
  const origin = from === null ? undefined : BACK_ORIGINS[from];
  if (!origin) return null;

  const gsi = params.get("gsi") || "";
  const target = origin.appendInstanceQuery
    ? `${origin.route}${gsi ? `?instanceid=${encodeURIComponent(gsi)}` : ""}`
    : `${origin.route}${gsi && origin.appendInstanceToPath ? `/${gsi}` : ""}`;

  return (
    <div className="px-6 pt-4">
      <SkyButton
        appearance="transparent"
        size="small"
        onClick={() => navigate(target)}
      >
        &#8592; {origin.label}
      </SkyButton>
    </div>
  );
};

export default GlobalSetupBackLink;
